
import { Component, Vue } from 'vue-property-decorator';
import ExcelJS from 'exceljs';
import { ErrorEnum } from '@/models/errorEnum';
import { TextBoxEnum } from '@/models/textBoxEnum';
import { ErrorG2000File } from '@/modules/results/models/errorG2000File';
import { ErrorRadionuclide } from '@/modules/results/models/errorRadionuclide';
import { ErrorFile } from '@/modules/results/models/errorFile';
import TextBoxCard, { TextBoxData } from '@/components/textbox/TextBoxCard.vue';
import Dialog, { DialogData } from '@/components/dialog/Dialog.vue';
import { mergeG2000Results } from '@/modules/results/controllers/g2000Result';

@Component({
  components: { Dialog, TextBoxCard }
})
export default class G2000 extends Vue {
  protected dragover = false;
  protected uploadedFiles: File[] = [];
  protected dialogError: DialogData = {
    isOpen: false,
    title: '',
    content: ''
  };
  protected isDownloadReady = false;
  protected inputFiles!: HTMLInputElement;
  protected excel!: ExcelJS.Buffer;
  protected readonly EXPORT_FILENAME = 'results.xlsx';
  protected textBoxData: TextBoxData = {
    title: TextBoxEnum.ERROR,
    content: ''
  };

  protected mounted(): void {
    this.inputFiles = document.createElement('input');
    this.inputFiles.accept = '.RPT';
    this.inputFiles.type = 'file';
    this.inputFiles.multiple = true;
    this.inputFiles.oninput = this.onInputFiles;
  }

  /**
   * The user has dropped files into the drag and drop card.
   */
  protected onDropFiles(e: DragEvent): void {
    this.dragover = false;
    if (e?.dataTransfer?.files && e.dataTransfer.files.length > 0) {
      this.saveFiles(e.dataTransfer.files);
    }
  }

  /**
   * The user has input files after clicked into the drag and drop card. Reset inputFiles value.
   */
  protected onInputFiles(): void {
    this.saveFiles(this.inputFiles.files);
    this.inputFiles.value = '';
  }

  /**
   * The user has input or dropped files. Checks and saves files.
   */
  protected saveFiles(files: FileList | null): void {
    if (files) {
      let tmpFileList = [];
      for (let index = 0; index < files.length; index++) {
        const file = files.item(index) as File;
        if (!file.name.endsWith('.RPT')) {
          this.openAndSetDialogErrorContent(new Error(ErrorEnum.INVALID_FILE_EXTENSION));
          return;
        }
        if (this.uploadedFiles.some((uploadedFile) => uploadedFile.name === file.name)) {
          this.openAndSetDialogErrorContent(new ErrorFile(ErrorEnum.DUPLICATED_FILE_NAME, file.name));
          return;
        }
        tmpFileList.push(file);
      }
      tmpFileList.sort((a, b) => a.name.localeCompare(b.name));
      this.uploadedFiles = this.uploadedFiles.concat(tmpFileList);
    }
  }

  protected merge(): void {
    mergeG2000Results(this.uploadedFiles)
      .then((res) => {
        this.excel = res;
        this.isDownloadReady = true;
        this.textBoxData.title = TextBoxEnum.SUCCESS;
        this.textBoxData.content = this.$i18n.t(`results.Merger.G2000.successMessage`).toString();
      })
      .catch((error: Error) => {
        this.openAndSetDialogErrorContent(error);
      });
  }

  protected openAndSetDialogErrorContent(error: Error): void {
    if (!Object.keys(ErrorEnum).includes(error.message)) {
      error.message = ErrorEnum.INTERNAL_SERVER_ERROR;
      this.dialogError.title = this.$i18n.t(`common.InternalError.fileError.${error.message}.title`).toString();
      this.dialogError.content = this.$i18n.t(`common.InternalError.fileError.${error.message}.content`).toString();
    } else {
      this.dialogError.title = this.$i18n.t(`results.Merger.G2000.fileError.${error.message}.title`).toString();
      this.dialogError.content = this.getG2000MessageByError(error);
    }
    this.dialogError.isOpen = true;
  }

  protected getG2000MessageByError(error: Error): string {
    let message = '';
    if (error instanceof ErrorG2000File) {
      const errG2000 = error as ErrorG2000File;
      message = this.$i18n
        .t(`results.Merger.G2000.fileError.${error.message}.content`, {
          fileName: errG2000.fileName,
          unitsReferenceFileName: errG2000.unitsReferenceFileName
        })
        .toString();
    } else if (error instanceof ErrorRadionuclide) {
      const errorRadionuclide = error as ErrorRadionuclide;
      message = this.$i18n
        .t(`results.Merger.G2000.fileError.${error.message}.content`, {
          rnName: errorRadionuclide.rnName
        })
        .toString();
    } else if (error instanceof ErrorFile) {
      const errorFile = error as ErrorFile;
      message = this.$i18n
        .t(`results.Merger.G2000.fileError.${error.message}.content`, {
          fileName: errorFile.fileName
        })
        .toString();
    } else {
      message = this.$i18n.t(`results.Merger.G2000.fileError.${error.message}.content`).toString();
    }

    return message;
  }

  protected downloadCsv(): void {
    let hiddenElement = document.createElement('a');
    const data = new Blob([this.excel], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    hiddenElement.href = URL.createObjectURL(data);
    hiddenElement.target = '_blank';
    hiddenElement.download = this.EXPORT_FILENAME;
    hiddenElement.click();
  }

  protected removeFile(fileName: string): void {
    this.uploadedFiles = this.uploadedFiles.filter((file) => file.name !== fileName);
  }

  protected reset(): void {
    this.isDownloadReady = false;
    this.uploadedFiles = [];
  }
}
